import { useState, useMemo, useEffect } from "react"
import type { POI } from "@/types/explore"

const CATEGORY_COLORS: Record<string, string> = {
  Transports: "border-primary",
  Commerces: "border-amber-500",
  Santé: "border-green-500",
  Écoles: "border-yellow-600",
  Monuments: "border-red-500",
}

export const CATEGORY_MARKER_COLORS: Record<string, string> = {
  Transports: "#3b82f6",
  Commerces: "#f59e0b",
  Santé: "#22c55e",
  Écoles: "#ca8a04",
  Monuments: "#ef4444",
  Autres: "#9ca3af",
}

const getCategoryLabel = (poi: POI): string => {
  const transports = ["subway", "SNCF", "RATP", "station", "tram_stop"]
  const healthServices = ["hospital", "doctors", "pharmacy"]
  const shops = ["supermarket", "convenience", "bakery"]
  const tags = [poi.tags?.amenity, poi.tags?.station, poi.tags?.operator, poi.tags?.railway, poi.tags?.shop]

  if (tags.includes("monument")) return "Monuments"
  if (tags.includes("school")) return "Écoles"
  if (tags.some((tag) => tag && transports.includes(tag))) return "Transports"
  if (tags.some((tag) => tag && healthServices.includes(tag))) return "Santé"
  if (tags.some((tag) => tag && shops.includes(tag))) return "Commerces"
  return "Autres"
}

export interface POIMarkerLocal {
  lon: number
  lat: number
  id: string
  category: string
}

interface PoiListProps {
  pois: POI[]
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  onFlyTo?: (center: [number, number]) => void
  onActivePOIsChange?: (pois: POIMarkerLocal[]) => void
  onHoverPOI?: (poi: POIMarkerLocal | null) => void
}

export const PoiList = ({ pois, setParcelPOI, onFlyTo, onActivePOIsChange, onHoverPOI }: PoiListProps) => {
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set())

  const poisWithCategory = useMemo(() => pois.map((p) => ({ ...p, _category: getCategoryLabel(p) })), [pois])

  const categories = useMemo(() => Array.from(new Set(poisWithCategory.map((p) => p._category))), [poisWithCategory])

  const filtered = useMemo(
    () =>
      activeCategories.size > 0 ? poisWithCategory.filter((p) => activeCategories.has(p._category)) : poisWithCategory,
    [poisWithCategory, activeCategories],
  )

  useEffect(() => {
    if (activeCategories.size === 0) {
      onActivePOIsChange?.([])
      return
    }
    const markers = filtered
      .filter((p) => p.lon && p.lat)
      .map((p) => ({
        lon: p.lon!,
        lat: p.lat!,
        id: String(p.id),
        category: p._category,
      }))
    onActivePOIsChange?.(markers)
  }, [activeCategories, filtered])

  return (
    <div className="pt-1">
      <div className="flex flex-wrap gap-1 mb-2">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() =>
              setActiveCategories((prev) => {
                const next = new Set(prev)
                if (next.has(cat)) next.delete(cat)
                else next.add(cat)
                return next
              })
            }
            className={`px-1.5 py-px rounded-sm text-xs border transition-colors ${
              activeCategories.has(cat)
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-600 border-gray-300 hover:border-primary/60"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>
      <div className="space-y-1.5">
        {filtered.map((poi, i) => {
          const name = poi.tags?.name || poi.tags?.alt_name || poi.tags?.official_name
          if (!name) return null
          return (
            <div
              key={poi.id || i}
              className={`flex items-center gap-2 pl-2 border-l-2 cursor-pointer hover:bg-gray-50 rounded-r transition-colors ${
                CATEGORY_COLORS[poi._category] || "border-gray-300"
              }`}
              onClick={() => {
                if (poi.lon && poi.lat) {
                  onFlyTo?.([poi.lon, poi.lat])
                  setParcelPOI({ lon: poi.lon, lat: poi.lat })
                }
              }}
              onMouseEnter={() => {
                if (poi.lon && poi.lat) {
                  onHoverPOI?.({
                    lon: poi.lon,
                    lat: poi.lat,
                    id: String(poi.id),
                    category: poi._category,
                  })
                }
              }}
              onMouseLeave={() => onHoverPOI?.(null)}
            >
              <span className="text-sm text-gray-700 flex-1">{name}</span>
              <span className="text-xs font-medium tabular-nums w-10 text-right shrink-0 text-gray-400">
                {poi.distance ? `${Math.round(poi.distance)}m` : ""}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
